import { fetchWithAuth } from '../utils/fetchWithAuth';
import { unwrapResponse as unwrap } from '../utils/unwrapResponse';

export type SubdivisionType = 'department' | 'province' | 'county';

export interface Country {
  code: string;
  name: string;
  /** Stable code, not a label: the wording comes from the i18n catalog. */
  subdivision_type: SubdivisionType;
  /** False on ES and GB — the design shows an "awaiting validation" banner for them. */
  is_validated: boolean;
  is_active: boolean;
  sort_order: number | null;
}

export interface Expertise {
  id: number;
  /** D68: the market this value belongs to. `code` is unique within it, never globally. */
  country_code: string;
  code: string;
  name: string;
  is_active: boolean;
  sort_order: number | null;
  /** Several ids means the expertise is shared, which is what the detach warning keys on. */
  specialty_ids: number[];
}

export interface Specialty {
  id: number;
  /** D68: the market this value belongs to. `code` is unique within it, never globally. */
  country_code: string;
  code: string;
  name: string;
  is_active: boolean;
  sort_order: number | null;
  expertises: ListedExpertise[];
  /** Active users linked to this specialty. */
  technician_count: number;
  /** Every link pointing at it, inactive users and tickets included — what blocks a deletion (D78). */
  usage_count: number;
}

/** An expertise as the listing returns it; the cascade endpoint does not count usages. */
export type ListedExpertise = Expertise & { usage_count: number };

export interface Subdivision {
  id: number;
  country_code: string;
  code: string;
  name: string;
  postal_prefixes: string[] | null;
  travel_zone: string | null;
  is_active: boolean;
  country_name: string;
  country_subdivision_type: SubdivisionType;
  technician_count: number;
}

/** What the base actually did: the confirmation announced one of these, a race can yield another. */
export type RemovalOutcome = 'detached' | 'deleted' | 'deactivated';

export interface RemoveExpertiseResult {
  outcome: RemovalOutcome;
  expertise: Expertise | null;
  remainingSpecialties: Specialty[];
}

export interface RemoveSpecialtyResult {
  outcome: Exclude<RemovalOutcome, 'detached'>;
  specialty: Specialty;
}

export interface CreateSubdivisionInput {
  countryCode: string;
  code: string;
  name: string;
  postalPrefixes?: string[];
  travelZone?: string | null;
}

export type UpdateSubdivisionInput = Partial<{
  name: string;
  postalPrefixes: string[];
  travelZone: string | null;
  isActive: boolean;
}>;

export interface CreateSpecialtyInput {
  countryCode: string;
  code: string;
  name: string;
  sortOrder?: number | null;
}

export interface CreateExpertiseInput extends CreateSpecialtyInput {
  specialtyIds: number[];
}

export type UpdateNamedInput = Partial<{ name: string; isActive: boolean; sortOrder: number | null }>;

const BASE = 'admin/referentials';
const jsonHeaders = { 'Content-Type': 'application/json' };

const post = <T>(path: string, body: unknown, fallback: string): Promise<T> =>
  fetchWithAuth(`${BASE}/${path}`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify(body) }).then(
    (res) => unwrap<T>(res, fallback)
  );

const put = <T>(path: string, body: unknown, fallback: string): Promise<T> =>
  fetchWithAuth(`${BASE}/${path}`, { method: 'PUT', headers: jsonHeaders, body: JSON.stringify(body) }).then((res) =>
    unwrap<T>(res, fallback)
  );

export const fetchCountries = async (): Promise<Country[]> =>
  unwrap<Country[]>(await fetchWithAuth(`${BASE}/countries`), 'Failed to fetch countries');

export const fetchSubdivisions = async (): Promise<Subdivision[]> =>
  unwrap<Subdivision[]>(await fetchWithAuth(`${BASE}/subdivisions`), 'Failed to fetch subdivisions');

export const createSubdivision = (input: CreateSubdivisionInput): Promise<Subdivision> =>
  post<Subdivision>('subdivisions', input, 'Failed to create subdivision');

export const updateSubdivision = (id: number, data: UpdateSubdivisionInput): Promise<Subdivision> =>
  put<Subdivision>(`subdivisions/${id}`, data, 'Failed to update subdivision');

export const fetchSpecialties = async (): Promise<Specialty[]> =>
  unwrap<Specialty[]>(await fetchWithAuth(`${BASE}/specialties`), 'Failed to fetch specialties');

export const createSpecialty = (input: CreateSpecialtyInput): Promise<Specialty> =>
  post<Specialty>('specialties', input, 'Failed to create specialty');

export const updateSpecialty = (id: number, data: UpdateNamedInput): Promise<Specialty> =>
  put<Specialty>(`specialties/${id}`, data, 'Failed to update specialty');

/**
 * The cascade behind the technician record: active expertises hanging under any of the given
 * specialties, deduplicated. An empty list yields an empty result, never the whole referential —
 * so the caller must not call it with nothing selected and expect everything.
 */
export const fetchExpertisesForSpecialties = async (specialtyIds: number[]): Promise<Expertise[]> =>
  unwrap<Expertise[]>(
    await fetchWithAuth(`${BASE}/expertises?specialtyIds=${specialtyIds.join(',')}`),
    'Failed to fetch expertises'
  );

export const createExpertise = (input: CreateExpertiseInput): Promise<Expertise> =>
  post<Expertise>('expertises', input, 'Failed to create expertise');

export const updateExpertise = (id: number, data: UpdateNamedInput): Promise<Expertise> =>
  put<Expertise>(`expertises/${id}`, data, 'Failed to update expertise');

export const attachExpertise = (expertiseId: number, specialtyId: number): Promise<Expertise> =>
  post<Expertise>(`expertises/${expertiseId}/specialties`, { specialtyId }, 'Failed to attach expertise');

/** Detaches a shared expertise; on its last specialty, deletes it when unused, else deactivates it. */
export const removeExpertise = async (expertiseId: number, specialtyId: number): Promise<RemoveExpertiseResult> =>
  unwrap<RemoveExpertiseResult>(
    await fetchWithAuth(`${BASE}/expertises/${expertiseId}/specialties/${specialtyId}`, { method: 'DELETE' }),
    'Failed to remove expertise'
  );

/** Deletes the specialty and its exclusive expertises when nothing uses them, else deactivates it. */
export const removeSpecialty = async (id: number): Promise<RemoveSpecialtyResult> =>
  unwrap<RemoveSpecialtyResult>(
    await fetchWithAuth(`${BASE}/specialties/${id}`, { method: 'DELETE' }),
    'Failed to remove specialty'
  );
